/*
  FAQ help center
  - Topic links show one category (or all) instead of jumping to it.
  - The page banner search ([data-page-banner-search]) filters questions on
    this page instead of going to /search. Without JS both fall back to
    their normal link / form behaviour.
*/
if (!customElements.get('faq-help-center')) {
  class FaqHelpCenter extends HTMLElement {
    connectedCallback() {
      this.groups = Array.from(this.querySelectorAll('[data-faq-group]'));
      this.links = Array.from(this.querySelectorAll('[data-faq-filter]'));
      this.empty = this.querySelector('[data-faq-empty]');
      this.category = 'all';
      this.query = '';

      this.links.forEach((link) => {
        link.addEventListener('click', (event) => {
          event.preventDefault();
          this.setCategory(link.dataset.faqFilter);
        });
      });

      this.bindSearch();
    }

    bindSearch() {
      const form = document.querySelector('[data-page-banner-search]');
      const input = form && form.querySelector('input[name="q"]');
      if (!input) return;

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.setQuery(input.value);
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      // Filter once typing pauses; submit (above) still applies immediately.
      input.addEventListener(
        'input',
        debounce(() => this.setQuery(input.value), 300)
      );
    }

    setCategory(category) {
      this.category = category;
      this.links.forEach((link) => {
        const isActive = link.dataset.faqFilter === category;
        link.classList.toggle('is-active', isActive);
        if (isActive) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      });
      this.update();

      if (this.getBoundingClientRect().top < 0) {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    setQuery(value) {
      const query = value.trim().toLowerCase();
      if (query === this.query) return;
      this.query = query;
      // Search across every topic.
      if (query && this.category !== 'all') {
        this.setCategory('all');
        return;
      }
      this.update();
    }

    update() {
      let visibleTotal = 0;
      this.clearHighlights();

      this.groups.forEach((group) => {
        const inCategory = this.category === 'all' || group.dataset.category === this.category;
        let visibleInGroup = 0;

        group.querySelectorAll('.hp-faq__item').forEach((item) => {
          const matches = !this.query || item.textContent.toLowerCase().includes(this.query);
          const show = inCategory && matches;
          item.hidden = !show;
          if (show) visibleInGroup += 1;
          if (show && this.query) this.highlight(item, this.query);
        });

        group.hidden = visibleInGroup === 0;
        this.updateCount(group, visibleInGroup);
        visibleTotal += visibleInGroup;
      });

      if (this.empty) this.empty.hidden = visibleTotal > 0;
    }

    // Wraps each match in the item's text nodes in <mark>, leaving markup
    // (links, bold, the toggle icon) untouched.
    highlight(root, query) {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) =>
          node.nodeValue.toLowerCase().includes(query) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT,
      });
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);

      nodes.forEach((node) => {
        const text = node.nodeValue;
        const lower = text.toLowerCase();
        const fragment = document.createDocumentFragment();
        let start = 0;
        let index = lower.indexOf(query);

        while (index !== -1) {
          fragment.append(text.slice(start, index));
          const mark = document.createElement('mark');
          mark.className = 'faq-help__mark';
          mark.textContent = text.slice(index, index + query.length);
          fragment.append(mark);
          start = index + query.length;
          index = lower.indexOf(query, start);
        }

        fragment.append(text.slice(start));
        node.replaceWith(fragment);
      });
    }

    clearHighlights() {
      const parents = new Set();
      this.querySelectorAll('mark.faq-help__mark').forEach((mark) => {
        parents.add(mark.parentNode);
        mark.replaceWith(mark.textContent);
      });
      // Re-join the split text nodes so the next search matches across them.
      parents.forEach((parent) => parent.normalize());
    }

    updateCount(group, count) {
      const counter = group.querySelector('[data-faq-count]');
      if (!counter) return;
      const label = count === 1 ? counter.dataset.labelOne : counter.dataset.labelOther;
      counter.textContent = `${count} ${label}`;
    }
  }

  customElements.define('faq-help-center', FaqHelpCenter);
}
